import { graphql } from "babel-plugin-relay/macro";
import React from "react";
import { useFragment, useLazyLoadQuery, useMutation } from "react-relay";
import { useNavigate } from "react-router-dom";
import { useCardAdvanceTreeNavigation_Fragment$key } from "@generated/useCardAdvanceTreeNavigation_Fragment.graphql";
import { useCardAdvanceTreeNavigation_Query } from "@generated/useCardAdvanceTreeNavigation_Query.graphql";
import { useCardAdvanceTreeNavigation_StartContentNodeMutation } from "@generated/useCardAdvanceTreeNavigation_StartContentNodeMutation.graphql";
import { useCardAdvanceTreeNavigation_StartTreeMutation } from "@generated/useCardAdvanceTreeNavigation_StartTreeMutation.graphql";
import { useCardAdvanceContentNavigation } from "./useCardAdvanceContentNavigation";
import { getLearnOpportunityPath } from "../../../infecto-lms-webapp-impl/router/routes/auth/nodes.routes";
import { selectCorrectPathToContent } from "../../utils/selectCorrectPathToContent";

const QUERY = graphql`
	query useCardAdvanceTreeNavigation_Query($learnOpportunityId: ID!) {
		node(id: $learnOpportunityId) {
			id
			... on LearnOpportunityV2 {
				id
				...useCardAdvanceTreeNavigation_Fragment
			}
		}
	}
`;

const TREE_ADVANCEMENT_RESULT_FRAGMENT = graphql`
	fragment useCardAdvanceTreeNavigation_Fragment on LearnOpportunityV2 {
		id
		shortDescription
		root {
			id
			structureDefinition {
				title
				... on LearnOpportunityRootStructureDefinition {
					viewerTreeState {
						kind
						... on CanNotBeStartedViewerTreeState {
							reasonConfigTypes
						}
						... on IsStartedViewerTreeState {
							headContentId
						}
					}
				}
			}
		}
		structureDefinition {
			title
		}
		typeDefinition {
			definitionType
			... on LearnOpportunityELearningContentTypeDefinition {
				contentNodeAdvancementResult {
					status
				}
				containedElementTypes
				extension {
					... on LearnContentExtensionImpl {
						cmePoints
						headFirstElementType
					}
				}
			}
		}
	}
`;

const START_TREE_MUTATION = graphql`
	mutation useCardAdvanceTreeNavigation_StartTreeMutation($input: StartTreeInput!) {
		LearnV2 {
			startTree(input: $input) {
				learnOpportunity {
					id
					structureDefinition {
						... on LearnOpportunityRootStructureDefinition {
							viewerTreeState {
								kind
								... on IsStartedViewerTreeState {
									headContentId
								}
							}
						}
					}
					typeDefinition {
						... on LearnOpportunityELearningContentTypeDefinition {
							contentNodeAdvancementResult {
								status
							}
						}
					}
					# Possibly more fragments ...
				}
			}
		}
	}
`;

const START_CONTENT_NODE_MUTATION = graphql`
	mutation useCardAdvanceTreeNavigation_StartContentNodeMutation($input: StartContentNodeInput!) {
		LearnV2 {
			startContentNode(input: $input) {
				contentSubmission {
					id
				}
			}
		}
	}
`;

interface UseCardAdvanceTreeNavigationProps {
	contentNodeId: string;
}

export function useCardAdvanceTreeNavigation({ contentNodeId }: UseCardAdvanceTreeNavigationProps) {
	const navigate = useNavigate();

	// Load the main query
	const query = useLazyLoadQuery<useCardAdvanceTreeNavigation_Query>(
		QUERY,
		{ learnOpportunityId: contentNodeId },
		{ fetchPolicy: "network-only" },
	);

	const learnOpportunity = useFragment<useCardAdvanceTreeNavigation_Fragment$key>(
		TREE_ADVANCEMENT_RESULT_FRAGMENT,
		query.node,
	);

	const [startTree] =
		useMutation<useCardAdvanceTreeNavigation_StartTreeMutation>(START_TREE_MUTATION);
	const [startContentNode] = useMutation<useCardAdvanceTreeNavigation_StartContentNodeMutation>(
		START_CONTENT_NODE_MUTATION,
	);
	const isNoMultipleChoiceElement =
		!learnOpportunity?.typeDefinition.containedElementTypes?.includes("multipleChoice");
	const handleContentNavigation = useCardAdvanceContentNavigation({
		contentNodeId,
	});

	return React.useCallback(() => {
		const advanceContentStatus =
			learnOpportunity?.typeDefinition.contentNodeAdvancementResult?.status;
		if (advanceContentStatus == "BlockedByTree") {
			const viewerTreeState = learnOpportunity?.root?.structureDefinition.viewerTreeState?.kind;
			switch (viewerTreeState) {
				case "CanBeStarted": {
					const startAtContentNodeId =
						learnOpportunity?.typeDefinition?.definitionType === "content" &&
						(learnOpportunity?.typeDefinition?.extension?.cmePoints || -1) > 0
							? learnOpportunity?.id
							: null;

					startTree({
						variables: {
							input: {
								nodeId: contentNodeId,
								startAtContentNodeId,
							},
						},
						onCompleted: (startTreeResponse) => {
							const contentStatus =
								startTreeResponse.LearnV2.startTree?.learnOpportunity.typeDefinition
									?.contentNodeAdvancementResult?.status;

							if (contentStatus === "CanBeStarted") {
								// We also need to start the content node
								startContentNode({
									variables: {
										input: { contentNodeId: contentNodeId },
									},
									onCompleted: () => {
										{
											navigate(
												selectCorrectPathToContent(
													learnOpportunity?.root?.id,
													learnOpportunity?.id!, //eslint-disable-line @typescript-eslint/no-non-null-asserted-optional-chain
													isNoMultipleChoiceElement,
												),
											);
										}
									},
								});
								return;
							}

							// If the tree is started, just navigate
							if (
								startTreeResponse.LearnV2.startTree?.learnOpportunity.structureDefinition
									?.viewerTreeState?.kind === "IsStarted"
							) {
								return navigate(
									selectCorrectPathToContent(
										learnOpportunity?.root?.id,
										learnOpportunity?.id!, //eslint-disable-line @typescript-eslint/no-non-null-asserted-optional-chain
										isNoMultipleChoiceElement,
									),
								);
							}
						},
					});
					break;
				}

				case "CanNotBeStarted": {
					navigate(
						getLearnOpportunityPath(learnOpportunity?.root?.id ?? learnOpportunity?.id!), //eslint-disable-line @typescript-eslint/no-non-null-asserted-optional-chain
					);
					break;
				}

				case "IsStarted": {
					handleContentNavigation();
					break;
				}

				case "IsFinished": {
					navigate(
						selectCorrectPathToContent(
							learnOpportunity?.root?.id,
							learnOpportunity?.id!, //eslint-disable-line @typescript-eslint/no-non-null-asserted-optional-chain
							isNoMultipleChoiceElement,
						),
					);
					break;
				}

				default:
					// E.g. NotVisible, NotAtHead, ...
					break;
			}
		} else {
			handleContentNavigation();
		}
	}, [learnOpportunity, startTree, startContentNode, contentNodeId, navigate]);
}
